import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Plus,
  Trash2,
  Loader2,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Link2Off,
  Building2
} from 'lucide-react';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
const API = `${basePath}/api/live-affiliate-manage`;

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString();

const hostOf = (raw) =>
  String(raw || '').trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '').toLowerCase();

const call = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, { credentials: 'include', ...options });
  if (res.status === 403) throw new Error('Access denied — this page is available to the admin account only.');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Server error: ${res.status}`);
  return data;
};

const LiveAffiliateManage = () => {
  const [brands, setBrands] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [brand, setBrand] = useState(null);
  const [brandsLoading, setBrandsLoading] = useState(true);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 0, totalPages: 0 });
  const [page, setPage] = useState(0);
  const [affSearch, setAffSearch] = useState('');
  const [listLoading, setListLoading] = useState(false);

  const [addUrl, setAddUrl] = useState('');
  const [addPage, setAddPage] = useState('');
  const [adding, setAdding] = useState(false);

  const [confirm, setConfirm] = useState(null);   // affiliate row pending removal
  const [busyRow, setBusyRow] = useState(null);
  const [undo, setUndo] = useState(null);         // last removal, for one-click restore
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const debounce = useRef(null);

  // ------------------------------------------------------------- brands

  const loadBrands = useCallback(async (search) => {
    setBrandsLoading(true);
    try {
      const data = await call(`/brands?search=${encodeURIComponent(search || '')}&limit=300`);
      setBrands(data.brands || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  useEffect(() => { loadBrands(''); }, [loadBrands]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => loadBrands(brandSearch), 300);
    return () => clearTimeout(debounce.current);
  }, [brandSearch, loadBrands]);

  // ------------------------------------------------------- affiliate list

  const loadAffiliates = useCallback(async (brandUrl, pageNo, search) => {
    if (!brandUrl) return;
    setListLoading(true);
    try {
      const data = await call(
        `/affiliates?brandUrl=${encodeURIComponent(brandUrl)}&page=${pageNo}&pageSize=50&search=${encodeURIComponent(search || '')}`
      );
      setRows(data.affiliates || []);
      setMeta({ total: data.total || 0, page: data.page || 0, totalPages: data.totalPages || 0 });
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (brand) loadAffiliates(brand.brandUrl, page, affSearch);
  }, [brand, page, loadAffiliates]);   // affSearch is applied by its own debounce below

  useEffect(() => {
    if (!brand) return;
    const t = setTimeout(() => { setPage(0); loadAffiliates(brand.brandUrl, 0, affSearch); }, 300);
    return () => clearTimeout(t);
  }, [affSearch]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => {
    if (brand) loadAffiliates(brand.brandUrl, page, affSearch);
    loadBrands(brandSearch);
  };

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 6000); };

  // ------------------------------------------------------------- actions

  const doAdd = async (e) => {
    e?.preventDefault();
    setError('');
    if (!brand) { setError('Select a brand first.'); return; }
    setAdding(true);
    try {
      const data = await call('/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandUrl: brand.brandUrl, affiliateUrl: addUrl, pageUrl: addPage })
      });
      flash(data.action === 'updated'
        ? `${data.affiliateUrl} was already on ${brand.brandUrl} — its proof page was updated.`
        : `${data.affiliateUrl} attached to ${brand.brandUrl}.`);
      setAddUrl('');
      setAddPage('');
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const doRemove = async (row) => {
    setError('');
    setBusyRow(row.affiliateUrl);
    try {
      const data = await call('/remove', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandUrl: brand.brandUrl, affiliateUrl: row.affiliateUrl })
      });
      setUndo({ affiliateUrl: data.affiliateUrl, pageUrl: data.previousPageUrl });
      flash(`${data.affiliateUrl} detached from ${brand.brandUrl}.`);
      setConfirm(null);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const doUndo = async () => {
    if (!undo) return;
    setError('');
    try {
      // Only restorable when a proof page was recorded — re-attaching without
      // one would invent evidence that the row never had.
      if (!undo.pageUrl) throw new Error('That row had no proof page recorded, so it cannot be restored automatically. Re-add it with a page URL.');
      await call('/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandUrl: brand.brandUrl, affiliateUrl: undo.affiliateUrl, pageUrl: undo.pageUrl })
      });
      flash(`${undo.affiliateUrl} restored to ${brand.brandUrl}.`);
      setUndo(null);
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  // --------------------------------------------------------------- view

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Live Affiliates</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
          Attach or detach an affiliate by hand when the live checker got it wrong.
        </p>
      </div>

      {/* affiliate_clean rows are keyed by brand, not by account, so this is not
          a per-customer edit and the operator should know that before clicking. */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          These edits are <strong>domain-wide</strong>. Affiliate rows are shared by brand URL, so detaching
          an affiliate here removes it for every account that holds this brand — not just one customer.
        </p>
      </div>

      {error && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
          <span className="flex items-start gap-2"><XCircle className="mt-0.5 h-4 w-4 shrink-0" />{error}</span>
          <button onClick={() => setError('')} className="shrink-0 underline">dismiss</button>
        </div>
      )}

      {notice && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-900/40 dark:bg-green-950/30 dark:text-green-200">
          <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0" />{notice}</span>
          {undo && (
            <button onClick={doUndo} className="flex shrink-0 items-center gap-1 rounded border border-green-300 px-2 py-1 text-xs font-medium hover:bg-green-100 dark:border-green-800 dark:hover:bg-green-900/40">
              <RotateCcw className="h-3 w-3" /> Undo
            </button>
          )}
        </div>
      )}

      {/* ------------------------------------------------------- brand pick */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-slate-300">Brand</label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={brandSearch}
            onChange={(e) => setBrandSearch(e.target.value)}
            placeholder="Search brands by name or domain…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-slate-800">
          {brandsLoading ? (
            <div className="flex items-center justify-center gap-2 p-6 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading brands…
            </div>
          ) : brands.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">No brands match that search.</div>
          ) : (
            brands.map((b) => (
              <button
                key={b.brandUrl}
                onClick={() => { setBrand(b); setPage(0); setAffSearch(''); setUndo(null); }}
                className={`flex w-full items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 text-left text-sm last:border-b-0 hover:bg-gray-50 dark:border-slate-800 dark:hover:bg-slate-800/50 ${
                  brand?.brandUrl === b.brandUrl ? 'bg-blue-50 dark:bg-blue-950/40' : ''
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate font-medium text-gray-900 dark:text-white">{b.brandName || b.brandUrl}</span>
                  <span className="truncate text-xs text-gray-500 dark:text-slate-400">{b.brandUrl}</span>
                </span>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-slate-800 dark:text-slate-300">
                  {fmt(b.affiliateCount)} affiliates
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      {brand && (
        <>
          {/* ------------------------------------------------------- add form */}
          <form onSubmit={doAdd} className="rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
            <h2 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
              Add an affiliate to {brand.brandUrl}
            </h2>
            <p className="mb-4 text-xs text-gray-500 dark:text-slate-400">
              The proof page is the page on the affiliate&rsquo;s own site that promotes this brand. It is what the
              live checker stores, and it must be on the affiliate&rsquo;s domain.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
                placeholder="affiliate domain, e.g. casinomeister.com"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
              <input
                value={addPage}
                onChange={(e) => setAddPage(e.target.value)}
                placeholder={`proof page, e.g. https://${hostOf(addUrl) || 'affiliate.com'}/review/${brand.brandUrl.split('.')[0]}`}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>
            <button
              type="submit"
              disabled={adding || !addUrl.trim() || !addPage.trim()}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {adding ? 'Attaching…' : 'Attach affiliate'}
            </button>
          </form>

          {/* ---------------------------------------------------- the listing */}
          <div className="rounded-xl border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4 dark:border-slate-800">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {fmt(meta.total)} affiliate{meta.total === 1 ? '' : 's'} on {brand.brandUrl}
              </h2>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={affSearch}
                  onChange={(e) => setAffSearch(e.target.value)}
                  placeholder="Filter affiliates…"
                  className="rounded-lg border border-gray-300 py-1.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>

            {listLoading ? (
              <div className="flex items-center justify-center gap-2 p-10 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-gray-500">
                No affiliates {affSearch ? 'match that filter' : 'attached to this brand yet'}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-slate-800/50 dark:text-slate-400">
                    <tr>
                      <th className="px-4 py-2 font-medium">Affiliate</th>
                      <th className="px-4 py-2 font-medium">Proof page</th>
                      <th className="px-4 py-2 text-right font-medium">Organic</th>
                      <th className="px-4 py-2 text-right font-medium">Brands</th>
                      <th className="px-4 py-2 text-right font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-t border-gray-100 dark:border-slate-800">
                        <td className="px-4 py-2.5">
                          <a href={`https://${r.affiliateUrl}`} target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400">
                            {r.affiliateUrl}<ExternalLink className="h-3 w-3" />
                          </a>
                          {r.vertical && <div className="text-xs text-gray-500 dark:text-slate-400">{r.vertical}</div>}
                        </td>
                        <td className="max-w-xs px-4 py-2.5">
                          {r.pageUrl ? (
                            <a href={r.pageUrl} target="_blank" rel="noreferrer"
                               className="block truncate text-xs text-gray-600 hover:underline dark:text-slate-400" title={r.pageUrl}>
                              {r.pageUrl}
                            </a>
                          ) : (
                            /* Attached via brand_url with no jsonb mark — an older
                               import, not something the checker verified. */
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                              <Link2Off className="h-3 w-3" /> no proof page recorded
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-slate-300">{fmt(r.organicTraffic)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-500 dark:text-slate-400">{fmt(r.brandCount)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <button
                            onClick={() => setConfirm(r)}
                            disabled={busyRow === r.affiliateUrl}
                            className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/40"
                          >
                            {busyRow === r.affiliateUrl
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <Trash2 className="h-3 w-3" />}
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {meta.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 p-3 text-sm dark:border-slate-800">
                <span className="text-gray-500 dark:text-slate-400">Page {meta.page + 1} of {meta.totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={meta.page === 0}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1 disabled:opacity-40 dark:border-slate-700">
                    <ChevronLeft className="h-4 w-4" /> Prev
                  </button>
                  <button onClick={() => setPage((p) => p + 1)} disabled={meta.page + 1 >= meta.totalPages}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1 disabled:opacity-40 dark:border-slate-700">
                    Next <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ------------------------------------------------------- confirm box */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 dark:bg-slate-900">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
              <AlertTriangle className="h-5 w-5 text-red-500" /> Detach this affiliate?
            </h3>
            <p className="mt-3 text-sm text-gray-600 dark:text-slate-400">
              <strong className="text-gray-900 dark:text-white">{confirm.affiliateUrl}</strong> will be removed from{' '}
              <strong className="text-gray-900 dark:text-white">{brand.brandUrl}</strong> for every account holding
              this brand. Its rows for other brands are untouched.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirm(null)}
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-slate-700 dark:text-slate-300">
                Cancel
              </button>
              <button onClick={() => doRemove(confirm)} disabled={busyRow === confirm.affiliateUrl}
                      className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {busyRow === confirm.affiliateUrl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Detach
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveAffiliateManage;
